import { Controller } from "@hotwired/stimulus"

// A brand's numbering as a build-your-own menu (brands/_number_menu): pick a
// code at each step, and the order ticket writes the number the brand's way
// and shows the figure it is, if there's one.
//
// - Picking a grade or series keeps the number you had when a figure has it
//   with your new picks, else takes the first number there is for them.
// - Step 3 offers only the numbers there are for your picks; taking one takes
//   that figure's whole number (its version too).
// - An optional step (a version) is picked again to leave it off.
//
// assembly: how each of the brand's patterns writes a number ([{series, style, pad}]);
// picks: what it opens on ({prefix: "MAG", series: "G", number: "0006"}).
export default class extends Controller {
  static targets = ["item", "number", "none", "out", "figure", "nofigure"]
  static values = { assembly: Array, picks: Object }

  connect() {
    this.state = { ...this.picksValue }
    this.render()
  }

  pick(event) {
    const { part, code, optional } = event.currentTarget.dataset
    if (optional === "true" && this.state[part] === code) delete this.state[part]
    else this.state[part] = code
    if (part === "prefix" || part === "series") this.keepOrFindNumber()
    this.render()
  }

  pickNumber(event) {
    this.state = { ...JSON.parse(event.currentTarget.dataset.picks) }
    this.render()
  }

  // The numbers there are for the current grade and series.
  available() {
    return this.numberTargets.filter((b) => {
      const p = JSON.parse(b.dataset.picks)
      return p.prefix === this.state.prefix && p.series === this.state.series
    })
  }

  keepOrFindNumber() {
    const there = this.available()
    if (there.some((b) => JSON.parse(b.dataset.picks).number === this.state.number)) return
    if (there.length) this.state.number = JSON.parse(there[0].dataset.picks).number
  }

  // The number written the way the pattern for its series writes it.
  written() {
    const pattern = this.assemblyValue.find((a) => a.series.includes(this.state.series)) || this.assemblyValue[0] || {}
    const digits = String(parseInt(this.state.number || "1", 10))
    const values = { ...this.state, number: pattern.pad ? digits.padStart(pattern.pad, "0") : digits }
    const style = pattern.style || "{prefix} {series}{number}{ suffix}"
    return style.replace(/\{([^{}a-z]*)([a-z]+)([^{}a-z]*)\}/g, (_, before, name, after) => values[name] ? `${before}${values[name]}${after}` : "")
  }

  render() {
    this.itemTargets.forEach((b) => b.setAttribute("aria-pressed", this.state[b.dataset.part] === b.dataset.code))
    const there = this.available()
    this.numberTargets.forEach((b) => {
      b.hidden = !there.includes(b)
      b.setAttribute("aria-pressed", there.includes(b) && JSON.parse(b.dataset.picks).number === this.state.number)
    })
    this.noneTarget.hidden = there.length > 0
    this.outTarget.textContent = this.written()

    const exact = there.find((b) => {
      const p = JSON.parse(b.dataset.picks)
      return p.number === this.state.number && (p.suffix || null) === (this.state.suffix || null)
    })
    this.figureTargets.forEach((f) => { f.hidden = !exact || f.dataset.figure !== exact.dataset.figure })
    this.nofigureTarget.hidden = Boolean(exact)
  }
}
